import type {
  DefinePageContextRouteOptions,
  PageContextPresentation,
  PageContextResource,
  PageContextRouteDescriptor,
  PageContextRouteMetadata,
} from './types';

interface RouteMatch {
  params: Record<string, string | undefined>;
  handle?: unknown;
}

interface PageContextRouteHandle {
  pageContext?: PageContextRouteMetadata;
}

export interface ResolvedPageContextRoute {
  type: string;
  presentation: Omit<PageContextPresentation, 'integration'>;
  resources: PageContextResource[];
}

/** Declare the stable Page Context facts owned by a route. */
export const definePageContextRoute = ({
  type,
  title,
  iconKey,
  resources,
}: DefinePageContextRouteOptions): PageContextRouteDescriptor => ({
  kind: 'page',
  type,
  presentation: { label: title, iconKey },
  ...(resources ? { resources } : {}),
});

const isPageContextRouteHandle = (handle: unknown): handle is PageContextRouteHandle =>
  typeof handle === 'object' && handle !== null && 'pageContext' in handle;

/**
 * Resolve the deepest Page Context declaration in the matched route chain.
 * Children inherit their parent's declaration unless they replace it.
 */
export const resolvePageContextRoute = (
  matches: RouteMatch[],
): ResolvedPageContextRoute | undefined => {
  let metadata: PageContextRouteMetadata | undefined;
  let params: Record<string, string | undefined> = {};

  matches.forEach((match) => {
    params = { ...params, ...match.params };
    if (isPageContextRouteHandle(match.handle) && match.handle.pageContext) {
      metadata = match.handle.pageContext;
    }
  });

  if (!metadata) {
    return undefined;
  }

  const resourceParams = metadata.resources ?? [];
  // A resource page is not complete until every declared route parameter exists.
  if (!resourceParams.every(({ routeParam }) => Boolean(params[routeParam]))) {
    return undefined;
  }
  const resources: PageContextResource[] = resourceParams.map(({ routeParam, type, role }) => ({
    type,
    role,
    id: params[routeParam] as string,
  }));

  return {
    type: metadata.type,
    presentation: metadata.presentation,
    resources,
  };
};
